import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Container,
  TextField,
  IconButton,
  InputAdornment,
  Button,
  Typography,
  Alert,
  Link,
  Avatar,
  Paper,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { detectThemeSync } from '../utils/themeUtils';
import { getStaticFileUrl } from '../utils/staticUtils';
import useAuthPageTheme from '../hooks/useAuthPageTheme';
import MobileAuthShell from '../components/auth/MobileAuthShell';
import {
  AUTH_COPYRIGHT,
  authCopyrightSx,
} from '../components/auth/loginDarkTheme';
import LoginCredentialsStep from '../components/auth/LoginCredentialsStep';
import LoginOtpStep from '../components/auth/LoginOtpStep';
import LoginFaceStep from '../components/auth/LoginFaceStep';
import LoginTermsDialog from '../components/auth/LoginTermsDialog';
import AuthBrandingPanel from '../components/auth/AuthBrandingPanel';
import { authFormPanelSxWithGlow } from '../components/auth/authLightPanelTheme';
import {
  appendRememberMe,
  buildLoginRequestHeaders,
  getDefaultRememberMe,
} from '../utils/loginSession';
import '../components/auth/authPages.css';
import './LoginPage.css';

const STEP_CREDENTIALS = 1;
const STEP_FACE = 2;
const STEP_OTP = 3;

/**
 * Login Page Component with Two-Step Flow
 * Step 1: Email/Username + Password
 * Step 2: OTP verification
 */
const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isCompact, formPanelThemeClass } = useAuthPageTheme();
  
  const [step, setStep] = useState(STEP_CREDENTIALS);
  const [hadFaceStep, setHadFaceStep] = useState(false);
  const [otpFallbackAvailable, setOtpFallbackAvailable] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const emailInputRef = useRef(null);
  const passwordInputRef = useRef(null);
  const credentialsSubmitLockRef = useRef(false);
  const flowHintTimerRef = useRef(null);
  const [loginFlowHint, setLoginFlowHint] = useState(null);
  const [termsDialogOpen, setTermsDialogOpen] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => getDefaultRememberMe());

  // Get CSRF token from cookie
  const getCsrfToken = () => {
    try {
      const cookies = document.cookie.split(';');
      const csrfCookie = cookies.find(c => c.trim().startsWith('csrftoken='));
      return csrfCookie ? decodeURIComponent(csrfCookie.split('=')[1] || '') : '';
    } catch {
      return '';
    }
  };

  const loginRequestConfig = () => ({
    headers: buildLoginRequestHeaders(getCsrfToken()),
    withCredentials: true,
  });

  // Validate email or username (username >= 5 chars OR valid email format)
  const validateEmailOrUsername = (value) => {
    if (!value || !value.trim()) {
      return 'Email or username is required';
    }
    const trimmed = value.trim();
    // Check if it's a valid email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emailRegex.test(trimmed)) {
      return null; // Valid email
    }
    // Check if it's a valid username (5 or more characters)
    if (trimmed.length >= 5) {
      return null; // Valid username
    }
    return 'Please enter a valid email address or a username with 5 or more characters';
  };

  // Handle credentials submission
  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    if (credentialsSubmitLockRef.current) {
      return;
    }
    credentialsSubmitLockRef.current = true;
    setIsSubmitting(true);
    setErrors({});

    const validationError = validateEmailOrUsername(email);
    if (validationError) {
      setErrors({ email: [validationError] });
      setIsSubmitting(false);
      credentialsSubmitLockRef.current = false;
      return;
    }

    try {
      const formData = new FormData();
      formData.append('action', 'credentials');
      formData.append('username', email); // Django auth uses 'username' field
      formData.append('password', password);
      appendRememberMe(formData, rememberMe);
      const nextParam = new URLSearchParams(location.search).get('next');
      if (nextParam) {
        formData.append('next', nextParam);
      }

      const response = await axios.post('/login/', formData, loginRequestConfig());

      if (response.data.success && response.data.face_required) {
        setHadFaceStep(true);
        setOtpFallbackAvailable(response.data.otp_fallback_available !== false);
        setStep(STEP_FACE);
        setOtp('');
        setInfoMessage(response.data.message || 'Verify your identity with face recognition.');
        setErrors({});
        return;
      }

      if (response.data.success && response.data.otp_required) {
        setStep(STEP_OTP);
        setOtp('');
        setMaskedEmail(response.data.masked_email || '');
        setInfoMessage(response.data.message || 'A verification code has been sent to your email.');
        setErrors({});
        return;
      }

      // On success with completed auth, redirect
      if (response.data.success) {
        const next = response.data.redirect || new URLSearchParams(location.search).get('next') || '/dashboard/';
        window.location.href = next;
      } else {
        setErrors(response.data.errors || { non_field_errors: ['Login failed.'] });
      }
    } catch (error) {
      if (error.response && error.response.data) {
        if (error.response.data.errors) {
          setErrors(error.response.data.errors);
          if (error.response.data.reset_to_password) {
            setStep(STEP_CREDENTIALS);
            setHadFaceStep(false);
            setOtp('');
            setInfoMessage('');
          }
        } else {
          setErrors({ non_field_errors: ['Invalid email or password.'] });
        }
      } else {
        setErrors({ non_field_errors: ['An error occurred. Please try again.'] });
      }
    } finally {
      setIsSubmitting(false);
      credentialsSubmitLockRef.current = false;
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleBack = () => {
    if (step === STEP_OTP) {
      setStep(hadFaceStep ? STEP_FACE : STEP_CREDENTIALS);
      setOtp('');
      setInfoMessage('');
      setErrors({});
      return;
    }
    if (step === STEP_FACE) {
      setStep(STEP_CREDENTIALS);
      setHadFaceStep(false);
      setOtp('');
      setInfoMessage('');
      setErrors({});
      return;
    }
    setStep(STEP_CREDENTIALS);
    setHadFaceStep(false);
    setPassword('');
    setOtp('');
    setMaskedEmail('');
    setInfoMessage('');
    setErrors({});
  };

  const handleFaceCapture = async (blob) => {
    setIsSubmitting(true);
    setErrors({});
    setInfoMessage('');

    try {
      const formData = new FormData();
      formData.append('action', 'verify_face');
      formData.append('face_image', blob, 'face.jpg');
      appendRememberMe(formData, rememberMe);

      const response = await axios.post('/login/', formData, loginRequestConfig());

      if (response.data.success) {
        const next = response.data.redirect || new URLSearchParams(location.search).get('next') || '/dashboard/';
        window.location.href = next;
        return;
      }

      setErrors(response.data.errors || { non_field_errors: ['Face verification failed.'] });
      if (response.data.otp_fallback_available === false) {
        setOtpFallbackAvailable(false);
      }
    } catch (error) {
      const data = error.response?.data || {};
      if (data.reset_to_password) {
        setStep(STEP_CREDENTIALS);
        setHadFaceStep(false);
        setOtp('');
        setInfoMessage('');
      }
      if (data.errors) {
        setErrors(data.errors);
        if (data.otp_fallback_available === false) {
          setOtpFallbackAvailable(false);
        }
      } else {
        setErrors({ non_field_errors: ['Unable to verify face. Please try again.'] });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUseOtpFallback = async () => {
    setIsSubmitting(true);
    setErrors({});
    setInfoMessage('');

    try {
      const formData = new FormData();
      formData.append('action', 'request_otp');
      appendRememberMe(formData, rememberMe);

      const response = await axios.post('/login/', formData, loginRequestConfig());

      if (response.data.success && response.data.otp_required) {
        setStep(STEP_OTP);
        setMaskedEmail(response.data.masked_email || maskedEmail);
        setInfoMessage(response.data.message || 'A verification code has been sent to your email.');
        setErrors({});
        return;
      }

      setErrors(response.data.errors || { non_field_errors: ['Unable to send OTP.'] });
    } catch (error) {
      const data = error.response?.data || {};
      if (data.reset_to_password) {
        setStep(STEP_CREDENTIALS);
        setHadFaceStep(false);
      }
      if (data.errors) {
        setErrors(data.errors);
      } else {
        setErrors({ non_field_errors: ['Unable to send OTP. Please try again.'] });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});
    setInfoMessage('');

    try {
      const formData = new FormData();
      formData.append('action', 'verify_otp');
      formData.append('otp', otp);
      appendRememberMe(formData, rememberMe);

      const response = await axios.post('/login/', formData, loginRequestConfig());

      if (response.data.success) {
        const next = response.data.redirect || new URLSearchParams(location.search).get('next') || '/dashboard/';
        window.location.href = next;
        return;
      }

      if (response.data.otp_required) {
        setStep(STEP_OTP);
      }
      setErrors(response.data.errors || { non_field_errors: ['OTP verification failed.'] });
    } catch (error) {
      const data = error.response?.data || {};
      if (data.reset_to_password) {
        setStep(hadFaceStep ? STEP_FACE : STEP_OTP);
        setOtp('');
        setInfoMessage('');
      }
      if (data.errors) {
        setErrors(data.errors);
      } else {
        setErrors({ non_field_errors: ['Unable to verify OTP. Please try again.'] });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    setIsSubmitting(true);
    setErrors({});
    setInfoMessage('');

    try {
      const formData = new FormData();
      formData.append('action', 'resend_otp');
      appendRememberMe(formData, rememberMe);

      const response = await axios.post('/login/', formData, loginRequestConfig());

      if (response.data.success && response.data.otp_required) {
        setStep(STEP_OTP);
        setMaskedEmail(response.data.masked_email || maskedEmail);
        setInfoMessage(response.data.message || 'A fresh OTP has been sent to your email.');
      } else {
        setErrors(response.data.errors || { non_field_errors: ['Unable to resend OTP.'] });
      }
    } catch (error) {
      const data = error.response?.data || {};
      if (data.reset_to_password) {
        setStep(STEP_CREDENTIALS);
        setHadFaceStep(false);
        setOtp('');
        setInfoMessage('');
      }
      if (data.errors) {
        setErrors(data.errors);
      } else {
        setErrors({ non_field_errors: ['Unable to resend OTP. Please try again.'] });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const isDark = detectThemeSync() === 'dark';

  // Browser autofill can bypass React onChange and leave labels overlapping values.
  // Sync autofilled DOM values into state after mount.
  useEffect(() => {
    const timer = setTimeout(() => {
      const autofilledEmail = (emailInputRef.current?.value || '').trim();
      const autofilledPassword = passwordInputRef.current?.value || '';
      if (autofilledEmail && !email) {
        setEmail(autofilledEmail);
      }
      if (autofilledPassword && !password) {
        setPassword(autofilledPassword);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [email, password]);

  useEffect(() => {
    const trimmed = (email || '').trim();
    if (flowHintTimerRef.current) {
      clearTimeout(flowHintTimerRef.current);
    }
    if (!trimmed || trimmed.length < 5) {
      setLoginFlowHint(null);
      return undefined;
    }

    flowHintTimerRef.current = setTimeout(async () => {
      try {
        const response = await axios.get('/login/', {
          params: { action: 'flow_hint', username: trimmed },
          withCredentials: true,
        });
        setLoginFlowHint(response.data || null);
      } catch {
        setLoginFlowHint(null);
      }
    }, 400);

    return () => {
      if (flowHintTimerRef.current) {
        clearTimeout(flowHintTimerRef.current);
      }
    };
  }, [email]);

  const credentialsSubmitLabel = useMemo(() => {
    if (
      loginFlowHint?.known_user
      && loginFlowHint.email_otp_required
      && !loginFlowHint.face_available
    ) {
      return 'Continue to OTP';
    }
    return 'Login';
  }, [loginFlowHint]);

  const credentialsSubmittingLabel = useMemo(() => {
    if (
      loginFlowHint?.known_user
      && loginFlowHint.email_otp_required
      && !loginFlowHint.face_available
    ) {
      return 'Sending OTP...';
    }
    return 'Signing in...';
  }, [loginFlowHint]);

  const credentialsHandlers = {
    emailInputRef,
    passwordInputRef,
    email,
    password,
    showPassword,
    rememberMe,
    errors,
    isSubmitting,
    onEmailChange: (e) => {
      setEmail(e.target.value);
      setErrors({});
    },
    onPasswordChange: (e) => {
      setPassword(e.target.value);
      setErrors({});
    },
    onTogglePassword: togglePasswordVisibility,
    onRememberMeChange: (checked) => {
      setRememberMe(Boolean(checked));
    },
    onSubmit: handleCredentialsSubmit,
    onOpenTerms: () => setTermsDialogOpen(true),
    onOpenFreeTrial: () => {
      const trimmedEmail = (email || '').trim();
      const query = trimmedEmail ? `?email=${encodeURIComponent(trimmedEmail)}` : '';
      navigate(`/free-trial/${query}`);
    },
    submitLabel: credentialsSubmitLabel,
    submittingLabel: credentialsSubmittingLabel,
  };

  const faceHandlers = {
    infoMessage,
    errors,
    isSubmitting,
    otpFallbackAvailable,
    onCapture: handleFaceCapture,
    onBack: handleBack,
    onUseOtp: handleUseOtpFallback,
  };

  const otpHandlers = {
    otp,
    maskedEmail,
    infoMessage,
    errors,
    isSubmitting,
    onOtpChange: (e) => {
      const value = (e.target.value || '').replace(/\D/g, '').slice(0, 6);
      setOtp(value);
      setErrors({});
    },
    onSubmit: handleOtpSubmit,
    onBack: handleBack,
    onResend: handleResendOtp,
  };

  if (isCompact) {
    return (
      <MobileAuthShell
        footer={
          <Typography variant="caption" sx={authCopyrightSx}>
            {AUTH_COPYRIGHT}
          </Typography>
        }
      >
        {step === STEP_CREDENTIALS && <LoginCredentialsStep variant="mobile-dark" {...credentialsHandlers} />}
        {step === STEP_FACE && <LoginFaceStep variant="mobile-dark" {...faceHandlers} />}
        {step === STEP_OTP && <LoginOtpStep variant="mobile-dark" {...otpHandlers} />}
        <LoginTermsDialog open={termsDialogOpen} onClose={() => setTermsDialogOpen(false)} />
      </MobileAuthShell>
    );
  }

  return (
    <Box
      className="login-page"
      sx={{
        display: 'flex',
        minHeight: '100vh',
        width: '100%',
        backgroundColor: '#ffffff',
      }}
    >
      <AuthBrandingPanel />

      {/* Right Panel - Login Form */}
      <Box
        className={`login-form-panel ${formPanelThemeClass}`}
        sx={{
          ...authFormPanelSxWithGlow,
          padding: { xs: 3, sm: 4, md: 6, lg: 8 },
        }}
      >
        <Container
          maxWidth="sm"
          sx={{
            width: '100%',
            px: { xs: 0, sm: 2 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            flex: 1,
            position: 'relative',
            zIndex: 1,
            '@media (min-width: 600px)': {
              maxWidth: '420px',
            },
          }}
        >
          {/* Step 1: Credentials Input */}
          {step === STEP_CREDENTIALS && (
            <Box
              sx={{
                animation: 'fadeInUp 0.5s ease-out',
                '@keyframes fadeInUp': {
                  '0%': {
                    opacity: 0,
                    transform: 'translateY(20px)',
                  },
                  '100%': {
                    opacity: 1,
                    transform: 'translateY(0)',
                  },
                },
              }}
            >
              {/* Brand Logo */}
              <Box
                component="img"
                src={getStaticFileUrl('topbar/logo_ed_dark.png')}
                alt="RoomNexa"
                sx={{
                  height: '48px',
                  width: 'auto',
                  mb: 5,
                  mx: 'auto',
                  display: 'block',
                  filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.08))',
                }}
              />

              <LoginCredentialsStep variant="desktop" {...credentialsHandlers} />
            </Box>
          )}

          {/* Step 2: Password Input */}
          {step === 99 && (
            <Box
              sx={{
                animation: 'fadeInUp 0.5s ease-out',
                '@keyframes fadeInUp': {
                  '0%': {
                    opacity: 0,
                    transform: 'translateY(20px)',
                  },
                  '100%': {
                    opacity: 1,
                    transform: 'translateY(0)',
                  },
                },
              }}
            >
              {/* Brand Logo */}
              <Box
                component="img"
                src={getStaticFileUrl('topbar/logo_ed_dark.png')}
                alt="RoomNexa"
                sx={{
                  height: '48px',
                  width: 'auto',
                  mb: 4,
                  mx: 'auto',
                  display: 'block',
                  filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.08))',
                }}
              />

              {/* User Display */}
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  mb: 3,
                  backgroundColor: 'linear-gradient(135deg, rgba(76, 225, 182, 0.08) 0%, rgba(76, 225, 182, 0.03) 100%)',
                  background: 'linear-gradient(135deg, rgba(76, 225, 182, 0.08) 0%, rgba(76, 225, 182, 0.03) 100%)',
                  borderRadius: '16px',
                  border: '1px solid rgba(76, 225, 182, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  boxShadow: '0 2px 12px rgba(76, 225, 182, 0.1)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    boxShadow: '0 4px 16px rgba(76, 225, 182, 0.15)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: '#4ce1b6',
                    width: 52,
                    height: 52,
                    fontSize: '20px',
                    fontWeight: 600,
                    boxShadow: '0 2px 8px rgba(76, 225, 182, 0.3)',
                  }}
                >
                  {email ? email.charAt(0).toUpperCase() : 'U'}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      color: '#212121',
                      fontSize: '17px',
                      mb: 0.5,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {email || 'User'}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#757575',
                      fontSize: '14px',
                      fontWeight: 400,
                    }}
                  >
                    Continue to sign in
                  </Typography>
                </Box>
              </Paper>

              {/* Error Messages */}
              {errors.non_field_errors && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(211, 47, 47, 0.15)',
                    '& .MuiAlert-icon': {
                      color: '#d32f2f',
                    },
                  }}
                >
                  {Array.isArray(errors.non_field_errors)
                    ? errors.non_field_errors.map((error, index) => <div key={index}>{error}</div>)
                    : errors.non_field_errors}
                </Alert>
              )}

              {/* Password Form */}
              <Box component="form" onSubmit={handleCredentialsSubmit} noValidate sx={{ width: '100%' }}>
                  {/* Password Input */}
                  <TextField
                    fullWidth
                    type={showPassword ? 'text' : 'password'}
                    label="Password"
                    name="password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setErrors({});
                    }}
                    required={false}
                    autoFocus
                    error={!!errors.password}
                    helperText={errors.password ? (Array.isArray(errors.password) ? errors.password.join(', ') : errors.password) : ''}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={togglePasswordVisibility}
                            edge="end"
                            sx={{
                              color: '#757575',
                              padding: '8px',
                              borderRadius: '8px',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                backgroundColor: 'rgba(76, 225, 182, 0.08)',
                                color: '#4ce1b6',
                              },
                            }}
                          >
                            {showPassword ? <VisibilityOff sx={{ fontSize: '20px' }} /> : <Visibility sx={{ fontSize: '20px' }} />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      mb: 3,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '0',
                        backgroundColor: '#fafafa',
                        fontSize: '16px',
                        height: '56px',
                        color: '#212121',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          backgroundColor: '#f5f5f5',
                          '& fieldset': {
                            borderColor: '#4ce1b6',
                            borderWidth: '2px',
                          },
                        },
                        '&.Mui-focused': {
                          backgroundColor: '#ffffff',
                          boxShadow: '0 0 0 4px rgba(76, 225, 182, 0.1)',
                          '& fieldset': {
                            borderColor: '#4ce1b6',
                            borderWidth: '2px',
                          },
                        },
                        '& fieldset': {
                          borderColor: '#e0e0e0',
                          borderWidth: '1px',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '15px',
                        color: '#757575',
                        '&.Mui-focused': {
                          color: '#4ce1b6',
                          fontWeight: 500,
                        },
                      },
                      '& .MuiInputBase-input': {
                        fontSize: '16px',
                        color: '#212121',
                        padding: '16.5px 16px',
                      },
                      '& .MuiFormHelperText-root': {
                        fontSize: '12px',
                        marginLeft: '4px',
                        marginTop: '6px',
                      },
                    }}
                  />

                  {/* Forgot Password Link */}
                  <Box sx={{ mt: -2, mb: 1, textAlign: 'right' }}>
                    <Link
                      href="/password-reset/"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate('/password-reset/');
                      }}
                      sx={{
                        color: '#4ce1b6',
                        textDecoration: 'none',
                        fontSize: '14px',
                        fontWeight: 500,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          textDecoration: 'underline',
                          color: '#3dd1a3',
                        },
                      }}
                    >
                      Forgot your password?
                    </Link>
                  </Box>

                  {/* Action Buttons */}
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 2,
                      mb: 0,
                    }}
                  >
                    {/* Back Button */}
                    <Button
                      type="button"
                      variant="outlined"
                      onClick={handleBack}
                      sx={{
                        py: 1.75,
                        px: 3,
                        backgroundColor: '#ffffff',
                        color: '#212121',
                        borderColor: '#e0e0e0',
                        borderRadius: '0',
                        textTransform: 'none',
                        fontSize: '16px',
                        fontWeight: 600,
                        height: '52px',
                        flex: 1,
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          backgroundColor: '#fafafa',
                          borderColor: '#bdbdbd',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
                          transform: 'translateY(-2px)',
                        },
                        '&:active': {
                          transform: 'translateY(0)',
                        },
                      }}
                    >
                      Back
                    </Button>

                    {/* Sign In Button */}
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isSubmitting}
                      sx={{
                        py: 1.75,
                        px: 3,
                        backgroundColor: '#000000',
                        color: '#ffffff',
                        borderRadius: '0',
                        textTransform: 'none',
                        fontSize: '16px',
                        fontWeight: 600,
                        height: '52px',
                        flex: 1,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          backgroundColor: '#1a1a1a',
                          boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
                          transform: 'translateY(-2px)',
                        },
                        '&:active': {
                          transform: 'translateY(0)',
                        },
                        '&:disabled': {
                          backgroundColor: '#bdbdbd',
                          color: '#ffffff',
                          transform: 'none',
                        },
                      }}
                    >
                      {isSubmitting ? 'Signing In...' : 'Login'}
                    </Button>
                  </Box>
                </Box>
            </Box>
          )}

          {/* Step 2: Face verification */}
          {step === STEP_FACE && (
            <Box
              sx={{
                animation: 'fadeInUp 0.5s ease-out',
                '@keyframes fadeInUp': {
                  '0%': {
                    opacity: 0,
                    transform: 'translateY(20px)',
                  },
                  '100%': {
                    opacity: 1,
                    transform: 'translateY(0)',
                  },
                },
              }}
            >
              <Box
                component="img"
                src={getStaticFileUrl('topbar/logo_ed_dark.png')}
                alt="RoomNexa"
                sx={{
                  height: '48px',
                  width: 'auto',
                  mb: 4,
                  mx: 'auto',
                  display: 'block',
                  filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.08))',
                }}
              />

              <LoginFaceStep variant="desktop" {...faceHandlers} />
            </Box>
          )}

          {/* Step 3: OTP Verification */}
          {step === STEP_OTP && (
            <Box
              sx={{
                animation: 'fadeInUp 0.5s ease-out',
                '@keyframes fadeInUp': {
                  '0%': {
                    opacity: 0,
                    transform: 'translateY(20px)',
                  },
                  '100%': {
                    opacity: 1,
                    transform: 'translateY(0)',
                  },
                },
              }}
            >
              <Box
                component="img"
                src={getStaticFileUrl('topbar/logo_ed_dark.png')}
                alt="RoomNexa"
                sx={{
                  height: '48px',
                  width: 'auto',
                  mb: 4,
                  mx: 'auto',
                  display: 'block',
                  filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.08))',
                }}
              />

              <LoginOtpStep variant="desktop" {...otpHandlers} />
            </Box>
          )}

          {/* Copyright - Centered at bottom */}
          <Box
            sx={{
              mt: 4,
              pt: 3,
              textAlign: 'center',
              width: '100%',
              position: 'relative',
              zIndex: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: '#6b7280',
                fontSize: '12px',
                fontWeight: 600,
                letterSpacing: '0.3px',
              }}
            >
              © 2025 RoomNexa, Inc. Singapore · Worldwide
            </Typography>
          </Box>
        </Container>
      </Box>
      <LoginTermsDialog open={termsDialogOpen} onClose={() => setTermsDialogOpen(false)} />
    </Box>
  );
};

export default LoginPage;
